import { KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { Popover } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';

import { UnderlineInput } from './fields';

import CalendarIcon from 'modules/common/components/Icons/CalendarIcon';

interface InvoiceDateFieldProps {
    /** `YYYY-MM-DD`. */
    value: string;
    onChange: (value: string) => void;
}

/** The invoice date, shown like the other underlined fields; clicking it opens a calendar. */
export default function InvoiceDateField({ value, onChange }: Readonly<InvoiceDateFieldProps>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
        }
    }, []);
    const handleClose = useCallback(() => setAnchorEl(null), []);
    const handleSelect = useCallback(
        (date: Dayjs | null) => {
            if (date) {
                onChange(date.format('YYYY-MM-DD'));
            }
            setAnchorEl(null);
        },
        [onChange]
    );

    return (
        <>
            <UnderlineInput
                readOnly
                value={dayjs(value).format('MMM D, YYYY')}
                onClick={handleOpen}
                onKeyDown={handleKeyDown}
                startAdornment={<CalendarIcon fontSize="small" sx={{ mr: 1 }} />}
                inputProps={{ 'aria-label': 'Invoice Date', 'aria-haspopup': 'dialog' }}
                sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar value={dayjs(value)} onChange={handleSelect} />
                </LocalizationProvider>
            </Popover>
        </>
    );
}
